import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Users, 
  LayoutGrid, 
  List,
  Phone,
  Mail,
  ArrowRight,
  Loader2,
  Hash
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }
};

const StatusBadge = ({ status, daysRemaining }) => {
  let config = {};

  if (status === 'expired' || daysRemaining < 0) {
    config = { class: 'nova-badge-danger', label: 'Vencido' };
  } else if (status === 'expiring' || (daysRemaining >= 0 && daysRemaining <= 7)) {
    config = { class: 'nova-badge-warning', label: `${daysRemaining}d` };
  } else {
    config = { class: 'nova-badge-success', label: 'Activo' };
  }

  return <span className={`nova-badge ${config.class}`}>{config.label}</span>;
};

const ClientCard = ({ client, memberships }) => {
  const membership = memberships.find(m => m.id === client.membership_id);
  const daysRemaining = client.membership_end 
    ? Math.ceil((new Date(client.membership_end) - new Date()) / (1000 * 60 * 60 * 24))
    : -1;

  return (
    <motion.div variants={item}>
      <Link 
        to={`/clients/${client.id}`}
        className="block nova-card nova-card-interactive p-6"
      >
        <div className="flex items-start justify-between mb-5">
          <div className="nova-avatar nova-avatar-lg">
            {client.name?.charAt(0)?.toUpperCase() || 'C'}
          </div>
          <StatusBadge status={client.status} daysRemaining={daysRemaining} />
        </div>
        
        <h3 className="text-base font-semibold text-white mb-1 truncate">
          {client.name}
        </h3>
        <p className="text-sm text-[#6e6e73] mb-5">{membership?.name || 'Sin membresía'}</p>
        
        <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-2">
            {client.phone && (
              <div className="w-8 h-8 rounded-lg bg-[#1c1c1f] flex items-center justify-center">
                <Phone className="w-3.5 h-3.5 text-[#6e6e73]" />
              </div>
            )}
            {client.email && (
              <div className="w-8 h-8 rounded-lg bg-[#1c1c1f] flex items-center justify-center">
                <Mail className="w-3.5 h-3.5 text-[#6e6e73]" />
              </div>
            )}
            <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center" title={`Código: ${client.access_code}`}>
              <Hash className="w-3.5 h-3.5 text-indigo-400" />
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#48484d] group-hover:text-indigo-400 transition-colors" />
        </div>
      </Link>
    </motion.div>
  );
};

const ClientRow = ({ client, memberships }) => {
  const membership = memberships.find(m => m.id === client.membership_id);
  const daysRemaining = client.membership_end 
    ? Math.ceil((new Date(client.membership_end) - new Date()) / (1000 * 60 * 60 * 24))
    : -1;

  return (
    <Link 
      to={`/clients/${client.id}`}
      className="nova-table-row flex items-center gap-4 p-4 hover:bg-[#161618] transition-colors group"
    >
      <div className="nova-avatar nova-avatar-md">
        {client.name?.charAt(0)?.toUpperCase() || 'C'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate group-hover:text-indigo-300 transition-colors">{client.name}</p>
        <p className="text-xs text-[#6e6e73]">{client.email || client.phone || 'Sin contacto'}</p>
      </div>
      <div className="hidden md:block text-sm text-[#a1a1a6] w-32 truncate">
        {membership?.name || '-'}
      </div>
      <div className="hidden md:flex items-center gap-1.5 w-24">
        <Hash className="w-3.5 h-3.5 text-indigo-400" />
        <span className="text-sm text-[#a1a1a6] font-mono">{client.access_code}</span>
      </div>
      <StatusBadge status={client.status} daysRemaining={daysRemaining} />
      <ArrowRight className="w-4 h-4 text-[#48484d] group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
    </Link>
  );
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '', email: '', phone: '', membership_id: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [clientsRes, membershipsRes] = await Promise.all([
        axios.get(`${API}/api/clients`, { headers }),
        axios.get(`${API}/api/memberships`, { headers }),
      ]);
      setClients(clientsRes.data);
      setMemberships(membershipsRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API}/api/clients`, newClient, { headers });
      setClients([data, ...clients]);
      setShowModal(false);
      setNewClient({ name: '', email: '', phone: '', membership_id: '' });
      toast.success('Cliente creado exitosamente');
    } catch (err) {
      toast.error('Error al crear cliente');
    } finally {
      setSaving(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(search.toLowerCase()) ||
      client.email?.toLowerCase().includes(search.toLowerCase()) ||
      client.phone?.includes(search) ||
      client.access_code?.includes(search);
    
    if (filter === 'all') return matchesSearch;
    const daysRemaining = client.membership_end 
      ? Math.ceil((new Date(client.membership_end) - new Date()) / (1000 * 60 * 60 * 24)) : -1;
    if (filter === 'active') return matchesSearch && daysRemaining > 7;
    if (filter === 'expiring') return matchesSearch && daysRemaining >= 0 && daysRemaining <= 7;
    if (filter === 'expired') return matchesSearch && daysRemaining < 0;
    return matchesSearch;
  });

  return (
    <Layout>
      <div data-testid="clients-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-headline text-white">Clientes</h1>
            <p className="text-caption mt-1">{clients.length} registrados</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="add-client-button"
            className="nova-btn nova-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nuevo Cliente
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6e6e73]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email, teléfono o código..."
              data-testid="search-clients"
              className="nova-input pl-11"
            />
          </div>
          <div className="flex gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-[#161618] border-white/[0.08] text-white h-11 rounded-xl" data-testid="filter-select">
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent className="bg-[#1c1c1f] border-white/[0.1] rounded-xl">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="expiring">Por vencer</SelectItem>
                <SelectItem value="expired">Vencidos</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex bg-[#161618] border border-white/[0.08] rounded-xl p-1">
              <button
                onClick={() => setView('grid')}
                className={`p-2.5 rounded-lg transition-all ${view === 'grid' ? 'bg-indigo-500/15 text-indigo-400' : 'text-[#6e6e73] hover:text-white'}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-2.5 rounded-lg transition-all ${view === 'list' ? 'bg-indigo-500/15 text-indigo-400' : 'text-[#6e6e73] hover:text-white'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Clients */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => <div key={i} className="h-48 nova-skeleton rounded-xl" />)}
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-16 h-16 rounded-2xl bg-[#1c1c1f] flex items-center justify-center mb-6">
              <Users className="w-8 h-8 text-[#48484d]" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {search ? 'Sin resultados' : 'Sin clientes'}
            </h3>
            <p className="text-sm text-[#6e6e73] mb-6">
              {search ? 'Intenta otra búsqueda' : 'Agrega tu primer cliente'}
            </p>
            {!search && (
              <button onClick={() => setShowModal(true)} className="nova-btn nova-btn-secondary">
                <Plus className="w-4 h-4" /> Crear cliente
              </button>
            )}
          </div>
        ) : view === 'grid' ? (
          <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredClients.map((client) => (
              <ClientCard key={client.id} client={client} memberships={memberships} />
            ))}
          </motion.div>
        ) : (
          <div className="nova-card overflow-hidden">
            <div className="hidden md:flex items-center gap-4 p-4 bg-[#111113] border-b border-white/[0.06] text-overline">
              <div className="w-10" />
              <div className="flex-1">Cliente</div>
              <div className="w-32">Membresía</div>
              <div className="w-24">Código</div>
              <div className="w-20">Estado</div>
              <div className="w-4" />
            </div>
            {filteredClients.map((client) => (
              <ClientRow key={client.id} client={client} memberships={memberships} />
            ))}
          </div>
        )}

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#161618] border-white/[0.1] text-white max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-title">Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-4">
              <div>
                <label className="text-caption block mb-2">Nombre *</label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  data-testid="new-client-name"
                  className="nova-input"
                  placeholder="Nombre completo"
                />
              </div>
              <div>
                <label className="text-caption block mb-2">Email</label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  data-testid="new-client-email"
                  className="nova-input"
                  placeholder="correo@email.com"
                />
              </div>
              <div>
                <label className="text-caption block mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  data-testid="new-client-phone"
                  className="nova-input"
                  placeholder="+52 55 1234 5678"
                />
              </div>
              <div>
                <label className="text-caption block mb-2">Membresía</label>
                <Select 
                  value={newClient.membership_id || "none"} 
                  onValueChange={(value) => setNewClient({ ...newClient, membership_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="w-full bg-[#111113] border-white/[0.08] text-white h-11 rounded-xl" data-testid="new-client-membership">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c1c1f] border-white/[0.1] rounded-xl">
                    <SelectItem value="none">Sin membresía</SelectItem>
                    {memberships.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name} - ${m.price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowModal(false)} className="flex-1 nova-btn nova-btn-secondary">
                  Cancelar
                </button>
                <button
                  onClick={handleCreateClient}
                  disabled={saving}
                  data-testid="save-client-button"
                  className="flex-1 nova-btn nova-btn-primary"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
